import React, { Suspense, useEffect, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ErrorBoundary, setContext, setTag, setUser } from "@sentry/react";
import { ProductFruits } from "react-product-fruits";
import { lazyWithPreload } from "./utils/lazyWithPreload";
import { postNotify, buildErrorContext } from "./utils/errorNotifier";
import { getStoredValue } from "src/services/logics.js";
import { getSSOProvider } from "./utils/ssoProvider.js";

// Non-lazy routes for critical UI that needs to load instantly
import Signin from "./pages/AuthPages/Signin";
import Signup from "./pages/AuthPages/Signup";
import ForgotPassword from "./pages/AuthPages/ForgotPassword";
import ResetPassword from "./pages/AuthPages/ResetPassword";
import VerifyEmail from "./pages/AuthPages/VerifyEmail";
import RotatingLoader from "./assets/RotatingLoader.jsx";
import Layout from "./layouts/ContentLayout";
import PrivateRoute from "./layouts/PrivateLayout";
import AdminLayout from "./layouts/AdminLayout";
import ProtectedAdminRoutes from "./layouts/ProtectedAdminRoutes";
// Lazy-loaded routes for better performance and optimized initial load
const Home = lazyWithPreload(() => import("./pages/Home"));
const DashboardPage = lazyWithPreload(() => import("./pages/Dashboard"));
const EmailsListPage = lazyWithPreload(() => import("./pages/EmailsListPage"));
const IntegrationPage = lazyWithPreload(() => import("./pages/IntegrationPage"));
const TemplatesPage = lazyWithPreload(() => import("./pages/TemplatePages/TemplatesPage"));
const TemplateDetailPage = lazyWithPreload(() => import("./pages/TemplatePages/TemplateDetailPage"));
const CreateTemplatesPage = lazyWithPreload(() => import("./pages/TemplatePages/CreateTemplatesPage"));
const AccountPage = lazyWithPreload(() => import("./pages/AccountPage"));
const DraftDetailPage = lazyWithPreload(() => import("./pages/DraftDetailPage"));
const ApplicationSetup = lazyWithPreload(() => import("./pages/OnboardingPages/ApplicationSetup"));
const ErrorPage = lazyWithPreload(() => import("./pages/ErrorPage"));
const AdminHome = lazyWithPreload(() => import("./pages/AdminPages/AdminHome"));
const AdminDashboard = lazyWithPreload(() => import("./pages/AdminPages/AdminDashboard"));
const AdminStorePage = lazyWithPreload(() => import("./pages/AdminPages/AdminStorePage"));
const AdminStorePageDetails = lazyWithPreload(() => import("./pages/AdminPages/AdminStorePageDetails"));
const AdminAnalytics = lazyWithPreload(() => import("./pages/AdminPages/AdminAnalytics"));
const PageNotFound = lazyWithPreload(() => import("./pages/PageNotFound"));
const Error502Page = lazyWithPreload(() => import("./pages/Error502Page"));
const PoliciesPage = lazyWithPreload(() => import("./pages/PoliciesPage/PoliciesPage"));
const SelectStore = lazyWithPreload(() => import("./pages/OnboardingPages/SelectStore"));
const SelectStoreIntro = lazyWithPreload(() => import("./pages/OnboardingPages/SelectStoreIntro"));
const AddClient = lazyWithPreload(() => import("./pages/OnboardingPages/AddClient"));
const SettingsPage = lazyWithPreload(() => import("./pages/SettingsPage/SettingPage"));
const SentryDebugPage = lazyWithPreload(() => import("./pages/SentryDebugPage"));

const isSentryDebugEnabled = process.env.ENVIRONMENT !== "production";

const preloadPages = () => {
  Home.preload();
  DashboardPage.preload();
  EmailsListPage.preload();
  IntegrationPage.preload();
  TemplatesPage.preload();
  TemplateDetailPage.preload();
  CreateTemplatesPage.preload();
  AccountPage.preload();
  DraftDetailPage.preload();
  PoliciesPage.preload();
  ApplicationSetup.preload();
  SelectStore.preload();
  SelectStoreIntro.preload();
  AddClient.preload();
  ErrorPage.preload();
  Error502Page.preload();
  PageNotFound.preload();
  AdminHome.preload();
  AdminDashboard.preload();
  AdminStorePage.preload();
  AdminStorePageDetails.preload();
  AdminAnalytics.preload();
  SettingsPage.preload();
};

const notifyError = async (error, componentStack, location) => {
  await postNotify(error, { componentStack, location });
};

const SentryRouteContext = () => {
  const location = useLocation();

  useEffect(() => {
    const context = buildErrorContext(location);
    const { route, session } = context;

    setTag("route_path", route.pathname || "unknown");
    if (session.store_id) {
      setTag("store_id", session.store_id);
    }

    setContext("route", route);
    setContext("frontend_session", session);

    if (session.user_id || session.user_email) {
      setUser({
        id: session.user_id,
        email: session.user_email,
      });
    }
  }, [location]);

  return null;
};

const ProductFruitsInit = () => {
  useLocation();
  const userId = getStoredValue("id");
  const email = getStoredValue("email") || undefined;
  const firstname = getStoredValue("firstname") || undefined;
  const lastname = getStoredValue("lastname") || undefined;
  const role = getStoredValue("role") || undefined;
  const workspaceCode = process.env.REACT_APP_PRODUCT_FRUITS_WORKSPACE_CODE;

  // props.brand: both brands share one ProductFruits workspace, and a tour's "Once per
  // user" auto-trigger is matched by URL alone - since both brands use the same routes,
  // a tour published for one brand auto-launches for the other brand's users too. This
  // custom property lets each tour's Audience settings be scoped to the right brand.
  const userInfo = useMemo(
    () => ({ username: userId, email, firstname, lastname, role, props: { brand: getSSOProvider() } }),
    [userId, email, firstname, lastname, role]
  );

  if (!userId || !workspaceCode) return null;

  // lifeCycle="unmount": without it, the component's cleanup skips tearing
  // down the ProductFruits runtime entirely, so React 18 StrictMode's
  // mount->cleanup->mount-again dance on first load leaves two copies of
  // ProductFruits running - each auto-triggering the same "Once per user"
  // tour, which is why Skip needed two clicks on a tour's first auto-launch.
  return <ProductFruits workspaceCode={workspaceCode} language="en" user={userInfo} lifeCycle="unmount" />;
};

const RouteLoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <RotatingLoader />
  </div>
);

const WrappedComponent = ({ children }) => {
  const location = useLocation();
  return (
    // A visible fallback, not an empty div: lazyWithPreload retries a failed
    // chunk up to 3 times with a 500ms delay before giving up, so a blank
    // fallback showed ~1.5s of empty white page - indistinguishable from the
    // app being broken - on exactly the slow/flaky connections where the retry
    // matters most.
    <Suspense fallback={<RouteLoadingFallback />}>
      <ErrorBoundary
        key={location.pathname}
        fallback={<PageNotFound text="An unexpected error occurred!" />}
        onError={(error, componentStack) => {
          notifyError(error, componentStack, location);
        }}
      >
        {children}
      </ErrorBoundary>
    </Suspense>
  );
};

function App() {
  useEffect(() => {
    preloadPages();
  }, []);

  return (
    <div>
      <Router>
        <SentryRouteContext />
        <ProductFruitsInit />
        <Routes>
          <Route path="/signin" element={<Signin />} />
          <Route path="/admin-signin" element={<Signin />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          <Route path="/" element={<PrivateRoute isAdminRoute={false} />}>
            <Route element={<Layout />}>
              <Route
                index
                element={
                  <WrappedComponent>
                    <Home />
                  </WrappedComponent>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <WrappedComponent>
                    <DashboardPage />
                  </WrappedComponent>
                }
              />

              <Route
                path="/select-store"
                element={
                  <WrappedComponent>
                    <SelectStore />
                  </WrappedComponent>
                }
              />
              <Route
                path="/intro-app"
                element={
                  <WrappedComponent>
                    <SelectStoreIntro />
                  </WrappedComponent>
                }
              />
              <Route
                path="/emails-page"
                element={
                  <WrappedComponent>
                    <EmailsListPage />
                  </WrappedComponent>
                }
              />
              <Route
                path="/email-page/detail/:id"
                element={
                  <WrappedComponent>
                    <DraftDetailPage />
                  </WrappedComponent>
                }
              />
              <Route
                path="/policies-page"
                element={
                  <WrappedComponent>
                    <PoliciesPage />
                  </WrappedComponent>
                }
              />
              <Route
                path="/integration-page"
                element={
                  <WrappedComponent>
                    <IntegrationPage />
                  </WrappedComponent>
                }
              />
              {/* The Examples feature was replaced by Templates. These redirects keep
                  existing bookmarks and in-app links working instead of dropping them
                  on PageNotFound; they can go once the old URLs stop appearing in
                  traffic. `replace` so the dead URL does not linger in history. */}
              <Route path="/examples-page" element={<Navigate to="/templates-page" replace />} />
              <Route path="/create-example/:id?" element={<Navigate to="/create-template" replace />} />
              <Route path="/example/:id?" element={<Navigate to="/templates-page" replace />} />
              <Route
                path="/templates-page"
                element={
                  <WrappedComponent>
                    <TemplatesPage />
                  </WrappedComponent>
                }
              />
              <Route
                path="/create-template/:id?"
                element={
                  <WrappedComponent>
                    <CreateTemplatesPage />
                  </WrappedComponent>
                }
              />
              <Route
                path="/template/:id?"
                element={
                  <WrappedComponent>
                    <TemplateDetailPage />
                  </WrappedComponent>
                }
              />
              <Route
                path="/settings-page/:tab?"
                element={
                  <WrappedComponent>
                    <SettingsPage />
                  </WrappedComponent>
                }
              />
              <Route
                path="/accounts-page"
                element={
                  <WrappedComponent>
                    <AccountPage />
                  </WrappedComponent>
                }
              />
              <Route
                path="/welcome-page/:status?"
                element={
                  <WrappedComponent>
                    <ApplicationSetup />
                  </WrappedComponent>
                }
              />
            </Route>
          </Route>

          <Route path="/admin" element={<ProtectedAdminRoutes />}>
            <Route element={<AdminLayout />}>
              <Route
                path=""
                element={
                  <WrappedComponent>
                    <AdminHome />
                  </WrappedComponent>
                }
              />
              <Route
                path="dashboard-page"
                element={
                  <WrappedComponent>
                    <AdminDashboard />
                  </WrappedComponent>
                }
              />
              <Route
                path="store-page"
                element={
                  <WrappedComponent>
                    <AdminStorePage />
                  </WrappedComponent>
                }
              />
              <Route
                path="store-page/details/:id"
                element={
                  <WrappedComponent>
                    <AdminStorePageDetails />
                  </WrappedComponent>
                }
              />
              <Route
                path="analytics-page"
                element={
                  <WrappedComponent>
                    <AdminAnalytics />
                  </WrappedComponent>
                }
              />
            </Route>
          </Route>

          <Route
            path="/502-error"
            element={
              <WrappedComponent>
                <Error502Page />
              </WrappedComponent>
            }
          />
          <Route
            path="/error"
            element={
              <WrappedComponent>
                <ErrorPage />
              </WrappedComponent>
            }
          />
          {isSentryDebugEnabled && (
            <Route
              path="/sentry-debug"
              element={
                <WrappedComponent>
                  <SentryDebugPage />
                </WrappedComponent>
              }
            />
          )}
          <Route
            path="*"
            element={
              <WrappedComponent>
                <PageNotFound />
              </WrappedComponent>
            }
          />
        </Routes>
      </Router>
    </div>
  );
}

export default App;
